import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import { Iconify } from 'src/components/iconify';
import { useOrganization, useSyncTenantFromParams } from '@/context/organization-context';
import { FlowBuilder } from '@/components/flows/flow-builder/FlowBuilder';
import { useFlowControllerGetDraft } from '@/apis/backend/generated/flows/flows';
import type { FlowDraftResponseDto } from '@/apis/backend/generated/models';

export const Route = createFileRoute('/_authed/dashboard_/o/$orgId/c/$clientId/flows/create')({
  component: FlowCreatePage,
  validateSearch: (search: Record<string, unknown>) => ({
    draftId: typeof search.draftId === 'string' ? search.draftId : undefined,
  }),
});

/** Email steps still being AI-generated (kind === 'email' && config.generating). */
function emailGenStats(draft?: FlowDraftResponseDto) {
  const nodes = draft?.graph?.nodes ?? [];
  const emails = nodes.filter(n => n.kind === 'email');
  const pending = emails.filter(n => (n.config as { generating?: boolean })?.generating);
  return {
    total: emails.length,
    ready: emails.length - pending.length,
    generating: pending.length > 0,
  };
}

function FlowCreatePage() {
  const { draftId } = Route.useSearch();
  const { orgId: routeOrgId, clientId: routeClientId } = Route.useParams();
  useSyncTenantFromParams(routeOrgId, routeClientId);
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const clientId = activeClient?.id ?? '';

  // No draftId → blank builder (manual authoring), unchanged behavior.
  if (!draftId) {
    return <FlowBuilder />;
  }
  return <GeneratedFlowCreate orgId={orgId} clientId={clientId} draftId={draftId} />;
}

function GeneratedFlowCreate({
  orgId,
  clientId,
  draftId,
}: {
  orgId: string;
  clientId: string;
  draftId: string;
}) {
  const { t } = useTranslation('flows');
  const { data: draft, isLoading } = useFlowControllerGetDraft(orgId, clientId, draftId, {
    query: {
      enabled: !!orgId && !!clientId,
      // Poll while content is still generating; stop once every email has landed.
      refetchInterval: query => {
        const d = query.state.data as FlowDraftResponseDto | undefined;
        return emailGenStats(d).generating ? 3000 : false;
      },
    },
  });

  const stats = emailGenStats(draft);

  // Show the builder once content generation has finished (or there were no
  // email steps to generate). Until then, a progress screen.
  if (!isLoading && draft && !stats.generating) {
    return <FlowBuilder initialDraft={draft} />;
  }

  const pct = stats.total > 0 ? Math.round((stats.ready / stats.total) * 100) : 0;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
      }}
    >
      <Stack spacing={2.5} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress size={56} thickness={4} />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify icon="solar:magic-stick-3-bold" width={24} sx={{ color: 'primary.main' }} />
          </Box>
        </Box>

        <Box>
          <Typography variant="h6">
            {t('generate.title', { defaultValue: 'Building your flow' })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('generate.subtitle', {
              defaultValue:
                'nexmail is writing each email in this flow. You can review and edit everything before publishing.',
            })}
          </Typography>
        </Box>

        <Box sx={{ width: '100%' }}>
          <LinearProgress
            variant={stats.total > 0 ? 'determinate' : 'indeterminate'}
            value={pct}
            sx={{ height: 8, borderRadius: 999 }}
          />
          {stats.total > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('generate.progress', {
                ready: stats.ready,
                total: stats.total,
                defaultValue: `${stats.ready} of ${stats.total} emails ready`,
              })}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
